import React, { useContext, useEffect, useState } from "react";
import { IMAGES } from "../assets";
import IconTint from "react-icon-tint";
import { ThemeContext } from "../context/ThemeProvider";
import { GET_EXAM_DISPLAY_TITLE } from "../../app_constant";
import { BundleContext } from "../context/BundleProvider";
import HTMLContent from "./HTMLContent";
import { getAppLogo } from "../api/theme_settings";
import { AuthContext } from "../context/AuthProvider";
import NotSubscribed from "../screens/NotSubscribed";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSun,
  faMoon,
  faUser,
  faExchangeAlt,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import useFeatureList from "../hooks/useFeatureList";
import SwitchExamButton from "./SwitchExamButton";
import theme_config from "../../config";
import { createLogger } from "../utils/logger";

function Header(props) {
  const [changeFont, setChangeFont] = useState(false);
  const { theme, currentTheme } = useContext(ThemeContext);
  const { selectedCertificate } = useContext(BundleContext);
  const [appName, setAppName] = useState("Exam Prep");
  const [logo, setLogo] = useState(null);
  const [blockApp, setBlockApp] = useState(false);
  const featureList = useFeatureList();

  const {
    showReactivation, mySubscription, loggingEnabled
  } = useContext(AuthContext);
  
  const logger = createLogger(loggingEnabled);

  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const displayTitle = await GET_EXAM_DISPLAY_TITLE(selectedCertificate);
        logger.log('[Header.useEffect] displayTitle:', displayTitle);
        setAppName(displayTitle);
      } catch (error) {
        logger.error('Error fetching exam display title:', error);
      }
    };
    fetchAppName();
  }, [selectedCertificate]);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const logoResult = await getAppLogo(currentTheme);
        setLogo(logoResult);
      } catch (error) {
        logger.error('Error fetching logo:', error);
        setLogo(null);
      }
    };
    fetchLogo();
  }, [currentTheme]);

  useEffect(() => {
    setTimeout(() => {
      logger.log('[Header.useEffect] showReactivation:', showReactivation);
      setBlockApp(showReactivation)
    }, 500)
  }, [showReactivation]);

  // Theme color helper functions
  const getHeaderBg = () => {
    return currentTheme === 'light'
      ? theme?.background?.headerLight
      : theme?.background?.headerDark;
  };

  const getPrimaryColor = () => {
    return theme?.primary?.main;
  };

  const getTextColor = () => {
    return currentTheme === 'light'
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  const getSecondaryColor = () => {
    return theme?.secondary?.main;
  };

  const getBorderColor = () => {
    return currentTheme === 'light'
      ? theme?.border?.light
      : theme?.border?.dark;
  };

  const getHoverBgColor = () => {
    // Creating a semi-transparent version of the primary color for hover states
    return currentTheme === 'light'
      ? theme?.secondary?.leftLight
      : theme?.secondary?.leftDark;
  };

  // Button style with theme colors
  const buttonStyle = {
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease',
    color: getTextColor(),
    borderRadius: '9999px', // Full rounded
  };

  const buttonHoverStyle = {
    ...buttonStyle,
    backgroundColor: getHoverBgColor(),
  };

  return (
    <div
      className="flex sticky top-0 z-[500] px-4 lg:px-8 lg:pr-12 py-3 shadow-sm grid grid-cols-2 gap-4 items-center"
      style={{
        background: getHeaderBg(),
        borderBottom: `1px solid ${getBorderColor()}`
      }}
    >
      {blockApp && <NotSubscribed 
        theme={theme}
        currentTheme={currentTheme}
      />}

      {/* Logo and App Title */}
      <div 
  className="relative text-2xl font-semibold py-2 px-1 mb-2 inline-block title-animation">
  <span style={{ 
    color: getPrimaryColor(),
    opacity: 1,
    transition: 'all 0.3s ease',
    letterSpacing: '0.5px',
    textShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
  }}>
    {appName}
    </span>
</div>

{/* Add a subtle animation for the title when page loads */}
<style jsx>{`
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .title-animation {
    animation: fadeIn 0.8s ease forwards;
  }
`}</style>

      {/* Action Buttons */}
      <div className="flex justify-end items-center space-x-3 lg:space-x-5">
        {/* Theme Toggle Button */}

      <SwitchExamButton theme={theme} currentTheme={currentTheme} />

        <div className="flex flex-col items-center">
          <button
            className="w-10 h-10 flex items-center justify-center"
            style={buttonStyle}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = getHoverBgColor();
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={props.onContactSupport}
            aria-label="Profile"
          >
            <FontAwesomeIcon
              icon={faEnvelope}
              className="text-xl"
              style={{ color: getTextColor() }}
            />
          </button>
          <span
            className="text-xs mt-1"
            style={{ color: getTextColor() }}
          >
            Contact Support
          </span>
        </div>

        {featureList?.home.dark_mode && props.onThemeChange && theme_config.available_modes.length > 1 && (
          <div className="flex flex-col items-center">
            <button
              className="w-10 h-10 flex items-center justify-center"
              style={buttonStyle}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = getHoverBgColor();
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={props.onThemeChange}
              aria-label={currentTheme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
            >
              <FontAwesomeIcon
                icon={currentTheme === "light" ? faMoon : faSun}
                className="text-xl"
                style={{ color: getTextColor() }}
              />
            </button>
            <span
              className="text-xs mt-1"
              style={{ color: getTextColor() }}
            >
              {currentTheme === "light" ? "Dark" : "Light"}
            </span>
          </div>
        )}


        {/* Profile Button */}
        {props.onProfile && (
          <div className="flex flex-col items-center">
            <button
              className="w-10 h-10 flex items-center justify-center"
              style={buttonStyle}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = getHoverBgColor();
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={props.onProfile}
              aria-label="Profile"
            >
              <FontAwesomeIcon
                icon={faUser}
                className="text-xl"
                style={{ color: getTextColor() }}
              />
            </button>
            <span
              className="text-xs mt-1"
              style={{ color: getTextColor() }}
            >
              Profile
            </span>
          </div>
        )}
      </div>
    </div>
  );
  
}

export default Header;